import type { FilterNode, LibraryItem } from '../curriculum/_lib/filter-engine'
import type { SortSpec } from '../curriculum/_lib/sorting'

export interface CompanyCount {
  name: string
  count: number
}

export interface CurriculumPreset {
  id: string
  label: string
  description: string
  typeFilter: 'all' | 'coding' | 'sd' | 'behavioral'
  filters: FilterNode[]
  sorts: SortSpec[]
}

export interface TopicRecommendation extends LibraryItem {
  fit: string
  rationale: string
}

export const MOCK_COMPANY_MAP: Record<string, string[]> = {
  'Kth Largest Element': ['Meta', 'Amazon'],
  'Merge K Sorted Lists': ['Google', 'Meta', 'Amazon'],
  'Task Scheduler': ['Meta'],
  'Course Schedule': ['Google', 'Uber'],
  'Binary Tree Max Path Sum': ['Google', 'Meta'],
  'Container With Most Water': ['Amazon'],
  'First Bad Version': ['Meta'],
  'Tell Me About a Recent Conflict': ['Amazon', 'Meta', 'Google'],
  'Trapping Rain Water': ['Google', 'Amazon'],
  'Search in Rotated Sorted Array': ['Meta', 'Netflix'],
  'Design a News Feed': ['Meta'],
  'Design a Chat System': ['Meta', 'Netflix'],
  'Design Consistent Hashing': ['Amazon', 'Netflix'],
  'A Time You Failed': ['Amazon', 'Google'],
  'House Robber III': ['Google'],
  'Design a Distributed Cache': ['Amazon', 'Netflix'],
  'Disagree with your manager': ['Amazon', 'Meta'],
  'Pushback you regret': ['Amazon'],
}

const withCompanies = <T extends Omit<LibraryItem, 'companies'>>(
  item: T,
): T & { companies: string[] } => ({
  ...item,
  companies: [...(MOCK_COMPANY_MAP[item.title] ?? [])],
})

export const MOCK_LIBRARY_ITEMS: LibraryItem[] = [
  withCompanies({
    id: 'kth-largest-element',
    title: 'Kth Largest Element',
    type: 'coding',
    topic: 'Heaps',
    subtopic: 'Top-K',
    difficulty: 'Med',
    stage: 'Phone',
    est: 1.5,
    elo: 1450,
    extId: '215',
    source: 'LC',
    plans: ['Grind 75', 'NeetCode 150', 'Taro 75'],
  }),
  withCompanies({
    id: 'merge-k-sorted-lists',
    title: 'Merge K Sorted Lists',
    type: 'coding',
    topic: 'Heaps',
    subtopic: 'K-way merge',
    difficulty: 'Hard',
    stage: 'Onsite',
    est: 2,
    elo: 1720,
    extId: '23',
    source: 'LC',
    plans: ['Grind 75', 'NeetCode 150'],
    repeat: 'solved 11 wks ago',
  }),
  withCompanies({
    id: 'task-scheduler',
    title: 'Task Scheduler',
    type: 'coding',
    topic: 'Heaps',
    subtopic: 'Greedy counting',
    difficulty: 'Med',
    stage: 'Onsite',
    est: 1.5,
    elo: 1580,
    extId: '621',
    source: 'LC',
    plans: ['NeetCode 150'],
  }),
  withCompanies({
    id: 'course-schedule',
    title: 'Course Schedule',
    type: 'coding',
    topic: 'Graphs',
    subtopic: 'Topo sort',
    difficulty: 'Med',
    stage: 'Onsite',
    est: 1.5,
    elo: 1540,
    extId: '207',
    source: 'LC',
    plans: ['Grind 75', 'NeetCode 150'],
  }),
  withCompanies({
    id: 'binary-tree-max-path-sum',
    title: 'Binary Tree Max Path Sum',
    type: 'coding',
    topic: 'Trees',
    subtopic: 'Memoization',
    difficulty: 'Hard',
    stage: 'Onsite',
    est: 2,
    elo: 1750,
    extId: '124',
    source: 'LC',
    plans: ['Grind 75'],
  }),
  withCompanies({
    id: 'container-with-most-water',
    title: 'Container With Most Water',
    type: 'coding',
    topic: 'Two pointers',
    subtopic: 'Converging',
    difficulty: 'Med',
    stage: 'Phone',
    est: 1,
    elo: 1490,
    extId: '11',
    source: 'LC',
    plans: ['Grind 75', 'NeetCode 150', 'BCtCI'],
  }),
  withCompanies({
    id: 'binary-search-template',
    title: 'Binary Search (template)',
    type: 'coding',
    topic: 'Binary search',
    subtopic: 'Vanilla',
    difficulty: 'Easy',
    stage: 'OA',
    est: 0.5,
    elo: 1150,
    extId: '704',
    source: 'LC',
    plans: ['BCtCI', 'NeetCode 150'],
  }),
  withCompanies({
    id: 'first-bad-version',
    title: 'First Bad Version',
    type: 'coding',
    topic: 'Binary search',
    subtopic: 'Predicate',
    difficulty: 'Easy',
    stage: 'OA',
    est: 0.5,
    elo: 1200,
    extId: '278',
    source: 'LC',
    plans: ['Grind 75', 'BCtCI'],
  }),
  withCompanies({
    id: 'tell-me-about-a-recent-conflict',
    title: 'Tell Me About a Recent Conflict',
    type: 'behavioral',
    topic: 'Conflict',
    subtopic: 'Teammate',
    difficulty: 'Med',
    stage: 'Onsite',
    est: 1,
    elo: 1430,
    extId: 'BH-02',
    plans: ['Taro Behavioral', 'BCtCI'],
  }),
  withCompanies({
    id: 'trapping-rain-water',
    title: 'Trapping Rain Water',
    type: 'coding',
    topic: 'Two pointers',
    subtopic: 'Prefix max',
    difficulty: 'Hard',
    stage: 'Onsite',
    est: 2,
    elo: 2150,
    extId: '42',
    source: 'LC',
    plans: ['NeetCode 150'],
  }),
  withCompanies({
    id: 'search-in-rotated-sorted-array',
    title: 'Search in Rotated Sorted Array',
    type: 'coding',
    topic: 'Binary search',
    subtopic: 'Rotated',
    difficulty: 'Med',
    stage: 'Phone',
    est: 1,
    elo: 1560,
    extId: '33',
    source: 'LC',
    plans: ['Grind 75', 'NeetCode 150'],
  }),
  withCompanies({
    id: 'design-a-news-feed',
    title: 'Design a News Feed',
    type: 'sd',
    topic: 'Classics',
    subtopic: 'Fan-out',
    difficulty: 'Med',
    stage: 'Onsite',
    est: 2,
    elo: 1500,
    extId: 'SD-08',
    plans: ['Taro SD'],
  }),
  withCompanies({
    id: 'design-a-chat-system',
    title: 'Design a Chat System',
    type: 'sd',
    topic: 'Classics',
    subtopic: 'Messaging',
    difficulty: 'Hard',
    stage: 'Onsite',
    est: 2.5,
    elo: 1700,
    extId: 'SD-12',
    plans: ['Taro SD', 'HelloInterview'],
  }),
  withCompanies({
    id: 'design-consistent-hashing',
    title: 'Design Consistent Hashing',
    type: 'sd',
    topic: 'Key tech',
    subtopic: 'Hashing',
    difficulty: 'Med',
    stage: 'Phone',
    est: 1.5,
    elo: 1520,
    extId: 'SD-03',
    plans: ['HelloInterview'],
    repeat: 'seen 8 wks ago',
  }),
]

export const MOCK_FEATURED_COMPANIES = [
  'Google',
  'Amazon',
  'Meta',
  'Netflix',
  'Uber',
]

export const MOCK_COMPANY_POOL: CompanyCount[] = [
  { name: 'Airbnb', count: 42 },
  { name: 'Apple', count: 118 },
  { name: 'Bloomberg', count: 96 },
  { name: 'Coinbase', count: 23 },
  { name: 'Databricks', count: 31 },
  { name: 'DoorDash', count: 38 },
  { name: 'Dropbox', count: 27 },
  { name: 'LinkedIn', count: 64 },
  { name: 'Lyft', count: 29 },
  { name: 'Microsoft', count: 142 },
  { name: 'Nvidia', count: 26 },
  { name: 'OpenAI', count: 14 },
  { name: 'Oracle', count: 51 },
  { name: 'Palantir', count: 22 },
  { name: 'Pinterest', count: 25 },
  { name: 'Salesforce', count: 47 },
  { name: 'Snap', count: 33 },
  { name: 'Stripe', count: 40 },
  { name: 'TikTok', count: 58 },
  { name: 'Twilio', count: 18 },
]

export const MOCK_PLANS = [
  'Grind 75',
  'NeetCode 150',
  'Taro 75',
  'Taro SD',
  'Taro Behavioral',
  'HelloInterview',
  'BCtCI',
]

export const MOCK_PRESETS: CurriculumPreset[] = [
  {
    id: 'easy-binary-search',
    label: 'Easy binary search',
    description:
      'Coding · topic contains "Binary search" and difficulty is Easy · easiest first',
    typeFilter: 'coding',
    filters: [
      {
        connector: 'where',
        field: 'topic',
        condition: 'contains',
        value: 'Binary search',
      },
      {
        connector: 'and',
        field: 'difficulty',
        condition: 'is',
        value: 'Easy',
      },
    ],
    sorts: [{ field: 'elo', dir: 'asc' }],
  },
  {
    id: 'all-bctci',
    label: 'All BCtCI',
    description: 'Every type · in plan BCtCI · by external ID',
    typeFilter: 'all',
    filters: [
      {
        connector: 'where',
        field: 'plan',
        condition: 'in plan',
        value: 'BCtCI',
      },
    ],
    sorts: [{ field: 'extId', dir: 'asc' }],
  },
  {
    id: 'grind-75-easiest-first',
    label: 'Grind 75 · easiest first',
    description: 'Coding · in plan Grind 75 · ELO ascending',
    typeFilter: 'coding',
    filters: [
      {
        connector: 'where',
        field: 'plan',
        condition: 'in plan',
        value: 'Grind 75',
      },
    ],
    sorts: [{ field: 'elo', dir: 'asc' }],
  },
  {
    id: 'hard-sd-classics',
    label: 'Hard SD classics',
    description:
      'System design · topic contains "Classics" and difficulty is Hard · top ELO first',
    typeFilter: 'sd',
    filters: [
      {
        connector: 'where',
        field: 'topic',
        condition: 'contains',
        value: 'Classics',
      },
      {
        connector: 'and',
        field: 'difficulty',
        condition: 'is',
        value: 'Hard',
      },
    ],
    sorts: [{ field: 'elo', dir: 'desc' }],
  },
  {
    id: 'behavioral-conflict',
    label: 'Behavioral · conflict',
    description: 'Behavioral · topic contains "Conflict"',
    typeFilter: 'behavioral',
    filters: [
      {
        connector: 'where',
        field: 'topic',
        condition: 'contains',
        value: 'Conflict',
      },
    ],
    sorts: [{ field: 'elo', dir: 'asc' }],
  },
]

export const MOCK_TOPIC_REASONS: Record<string, string> = {
  Heaps:
    'Trees finished last sprint — Heaps is the natural next structure and unlocks Top-K patterns',
  'Two pointers':
    'The Converging subtopic is at 74% forget risk — a focused Two pointers pass clears a live decay gap.',
  'Binary search':
    'Rotated arrays is the highest-risk decay item at 82% — Binary search is the most urgent refresh.',
  Caching: 'This week’s URL Shortener review exposed cache-eviction gaps',
  'Conflict stories':
    'Done with "TMAY" — conflict stories have the next-highest ROI for the Meta loop',
}

export const MOCK_TOPIC_RECOMMENDATIONS: Record<string, TopicRecommendation[]> =
  {
    Heaps: [
      withCompanies({
        id: 'kth-largest-element',
        title: 'Kth Largest Element',
        type: 'coding',
        fit: 'fit 92%',
        rationale: 'Solving Med heaps at ~78% — squarely in range.',
        est: 1.5,
        topic: 'Heaps',
        subtopic: 'Top-K',
        difficulty: 'Med',
        elo: 1450,
        extId: '215',
        source: 'LC',
        stage: 'Phone',
        plans: ['Grind 75', 'NeetCode 150', 'Taro 75'],
      }),
      withCompanies({
        id: 'task-scheduler',
        title: 'Task Scheduler',
        type: 'coding',
        fit: 'fit 88%',
        rationale:
          'Greedy + heap combo they haven’t seen; fills the counting-pattern gap.',
        est: 1.5,
        topic: 'Heaps',
        subtopic: 'Greedy counting',
        difficulty: 'Med',
        elo: 1580,
        extId: '621',
        source: 'LC',
        stage: 'Onsite',
        plans: ['NeetCode 150'],
      }),
      withCompanies({
        id: 'merge-k-sorted-lists',
        title: 'Merge K Sorted Lists',
        type: 'coding',
        fit: 'stretch',
        rationale: 'Hard — assign after the two Meds land.',
        est: 2,
        topic: 'Heaps',
        subtopic: 'K-way merge',
        difficulty: 'Hard',
        elo: 1720,
        extId: '23',
        source: 'LC',
        stage: 'Onsite',
        plans: ['Grind 75', 'NeetCode 150'],
      }),
    ],
    'Two pointers': [
      withCompanies({
        id: 'container-with-most-water',
        title: 'Container With Most Water',
        type: 'coding',
        fit: 'fit 90%',
        rationale: 'Also clears the Two Pointers decay item.',
        est: 1,
        topic: 'Two pointers',
        subtopic: 'Converging',
        difficulty: 'Med',
        elo: 1490,
        extId: '11',
        source: 'LC',
        stage: 'Phone',
        plans: ['Grind 75', 'NeetCode 150'],
      }),
      withCompanies({
        id: 'trapping-rain-water',
        title: 'Trapping Rain Water',
        type: 'coding',
        fit: 'stretch',
        rationale:
          'Classic hard — good week-after target once Container lands.',
        est: 2,
        topic: 'Two pointers',
        subtopic: 'Prefix max',
        difficulty: 'Hard',
        elo: 2150,
        extId: '42',
        source: 'LC',
        stage: 'Onsite',
        plans: ['NeetCode 150'],
      }),
    ],
    'Binary search': [
      withCompanies({
        id: 'search-in-rotated-sorted-array',
        title: 'Search in Rotated Sorted Array',
        type: 'coding',
        fit: 'fit 91%',
        rationale: 'Directly clears the highest-risk decay topic.',
        est: 1,
        topic: 'Binary search',
        subtopic: 'Rotated',
        difficulty: 'Med',
        elo: 1560,
        extId: '33',
        source: 'LC',
        stage: 'Phone',
        plans: ['Grind 75', 'NeetCode 150'],
      }),
    ],
    Caching: [
      withCompanies({
        id: 'design-a-distributed-cache',
        title: 'Design a Distributed Cache',
        type: 'sd',
        fit: 'fit 84%',
        rationale: 'Builds on the URL-shortener cache discussion from review.',
        est: 2,
        topic: 'Caching',
        subtopic: 'Distributed',
        difficulty: 'Med',
        elo: 1600,
        extId: 'SD-15',
        stage: 'Onsite',
        plans: ['Taro SD'],
      }),
      withCompanies({
        id: 'design-consistent-hashing',
        title: 'Design Consistent Hashing',
        type: 'sd',
        fit: 'fit 81%',
        rationale: 'Prereq for the cache deep dive; also in the decay queue.',
        est: 1.5,
        topic: 'Caching',
        subtopic: 'Hashing',
        difficulty: 'Med',
        elo: 1520,
        extId: 'SD-03',
        stage: 'Phone',
        plans: ['HelloInterview'],
      }),
    ],
    'Conflict stories': [
      withCompanies({
        id: 'disagree-with-your-manager',
        title: 'Disagree with your manager',
        type: 'behavioral',
        fit: 'fit 89%',
        rationale:
          'Story bank has no manager-conflict story — Meta will probe it.',
        est: 1,
        topic: 'Conflict',
        subtopic: 'Manager',
        difficulty: 'Med',
        elo: 1420,
        extId: 'BH-07',
        stage: 'Onsite',
        plans: ['Taro Behavioral'],
      }),
    ],
  }
